import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import {
  ChevronLeft, ChevronRight, CheckCircle, XCircle, AlertTriangle,
  Clock, Minus, Loader2, Truck, User, ArrowRightLeft, TrendingDown,
  BarChart3, Eye
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MONTH_NAMES = [
  'Janvier', 'Fevrier', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Aout', 'Septembre', 'Octobre', 'Novembre', 'Decembre'
];

const STATUS_CONFIG = {
  conform:      { icon: CheckCircle, color: 'bg-emerald-500', text: 'text-white', label: 'Conforme', border: 'border-emerald-200' },
  partial:      { icon: AlertTriangle, color: 'bg-amber-400', text: 'text-white', label: 'Ecart partiel', border: 'border-amber-200' },
  non_conform:  { icon: XCircle, color: 'bg-red-500', text: 'text-white', label: 'Non conforme', border: 'border-red-200' },
  planned:      { icon: Clock, color: 'bg-blue-400', text: 'text-white', label: 'Planifie', border: 'border-blue-200' },
  not_expected: { icon: Minus, color: 'bg-gray-100', text: 'text-gray-300', label: '', border: 'border-transparent' },
};

const DEVIATION_LABELS = {
  absent: 'Absence',
  vehicle_substitution: 'Substitution vehicule',
  driver_substitution: 'Substitution chauffeur',
  under_performance: 'Sous-realisation',
};

function DayCell({ dayData, dayNum }) {
  if (!dayData) return <td className="p-0.5"><div className="w-7 h-7" /></td>;

  const cfg = STATUS_CONFIG[dayData.status] || STATUS_CONFIG.not_expected;

  if (dayData.status === 'not_expected') {
    return (
      <td className="p-0.5">
        <div className="w-7 h-7 rounded bg-gray-50 border border-gray-100" />
      </td>
    );
  }

  return (
    <td className="p-0.5">
      <Popover>
        <PopoverTrigger asChild>
          <button
            data-testid={`day-cell-${dayNum}-${dayData.status}`}
            className={`w-7 h-7 rounded ${cfg.color} ${cfg.text} flex items-center justify-center text-[9px] font-bold cursor-pointer hover:ring-2 hover:ring-offset-1 hover:ring-gray-400 transition-all`}
          >
            {dayNum}
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-72 p-3" side="top">
          <DayDetail dayData={dayData} dayNum={dayNum} />
        </PopoverContent>
      </Popover>
    </td>
  );
}

function DayDetail({ dayData, dayNum }) {
  const cfg = STATUS_CONFIG[dayData.status];
  return (
    <div className="space-y-2.5">
      <div className="flex items-center gap-2">
        <div className={`w-6 h-6 rounded flex items-center justify-center ${cfg.color} ${cfg.text}`}>
          <cfg.icon className="w-3.5 h-3.5" />
        </div>
        <span className="text-sm font-semibold text-gray-900">{cfg.label}</span>
      </div>

      {dayData.deviations?.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {dayData.deviations.map((d, i) => (
            <Badge key={i} variant="outline" className="text-[10px] text-red-600 border-red-200">
              {DEVIATION_LABELS[d] || d}
            </Badge>
          ))}
        </div>
      )}

      {dayData.expected && (
        <div className="text-[11px] space-y-1 border-t border-gray-100 pt-2">
          <p className="font-semibold text-gray-500 uppercase text-[10px]">Attendu</p>
          {dayData.expected.vehicles?.length > 0 && (
            <div className="flex items-center gap-1.5">
              <Truck className="w-3 h-3 text-gray-400" />
              <span>{dayData.expected.vehicles.join(', ')}</span>
            </div>
          )}
          {dayData.expected.drivers?.length > 0 && (
            <div className="flex items-center gap-1.5">
              <User className="w-3 h-3 text-gray-400" />
              <span>{dayData.expected.drivers.join(', ')}</span>
            </div>
          )}
          <p className="text-gray-400">{dayData.expected.runsExpected} operation(s) attendue(s)</p>
        </div>
      )}

      {dayData.actual && (
        <div className="text-[11px] space-y-1 border-t border-gray-100 pt-2">
          <p className="font-semibold text-gray-500 uppercase text-[10px]">Realise</p>
          {dayData.actual.vehicles?.length > 0 && (
            <div className="flex items-center gap-1.5">
              <Truck className="w-3 h-3 text-gray-400" />
              <span>{dayData.actual.vehicles.join(', ')}</span>
            </div>
          )}
          {dayData.actual.drivers?.length > 0 && (
            <div className="flex items-center gap-1.5">
              <User className="w-3 h-3 text-gray-400" />
              <span>{dayData.actual.drivers.join(', ')}</span>
            </div>
          )}
          <p className="text-gray-400">{dayData.actual.runs} operation(s) realisee(s)</p>
        </div>
      )}

      {dayData.operations?.length > 0 && (
        <div className="text-[10px] border-t border-gray-100 pt-2 space-y-1">
          <p className="font-semibold text-gray-500 uppercase">Opérations</p>
          {dayData.operations.map((op, i) => (
            <div key={i} className="flex items-center justify-between text-gray-600">
              <span>{op.vehicle || '?'} / {op.driver || '?'}</span>
              <Badge variant="outline" className="text-[9px] h-4">{op.status}</Badge>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ComplianceGauge({ rate }) {
  const color = rate >= 80 ? 'text-emerald-600' : rate >= 50 ? 'text-amber-500' : 'text-red-500';
  const bg = rate >= 80 ? 'bg-emerald-100' : rate >= 50 ? 'bg-amber-100' : 'bg-red-100';
  return (
    <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full ${bg}`}>
      <span className={`text-sm font-bold ${color}`}>{rate}%</span>
    </div>
  );
}

export default function ComplianceTab({ contractId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [groupByTransporter, setGroupByTransporter] = useState(false);

  const fetchCompliance = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${API}/contracts/${contractId}/compliance?month=${year}-${String(month).padStart(2, '0')}`,
        {}
      );
      setData(res.data);
    } catch {
      setData(null);
    }
    setLoading(false);
  }, [contractId, year, month]);

  useEffect(() => { fetchCompliance(); }, [fetchCompliance]);

  const prevMonth = () => {
    if (month === 1) { setMonth(12); setYear(y => y - 1); }
    else setMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (month === 12) { setMonth(1); setYear(y => y + 1); }
    else setMonth(m => m + 1);
  };

  // Get days in month for header
  const daysInMonth = new Date(year, month, 0).getDate();
  const dayNumbers = Array.from({ length: daysInMonth }, (_, i) => i + 1);

  // Group slots by transporter if toggled
  const getSlotGroups = () => {
    if (!data?.slots) return [];
    if (!groupByTransporter) return [{ transporter: null, slots: data.slots }];

    const groups = {};
    data.slots.forEach(s => {
      const key = s.transporter || 'Non assigne';
      if (!groups[key]) groups[key] = { transporter: key, transporterId: s.transporterId, slots: [], totalExpected: 0, totalConform: 0 };
      groups[key].slots.push(s);
      groups[key].totalExpected += s.expectedDays;
      groups[key].totalConform += s.conformDays;
    });
    return Object.values(groups);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  if (data?.noSlots) {
    return (
      <div className="text-center py-16 text-gray-400">
        <BarChart3 className="w-10 h-10 mx-auto mb-3 text-gray-300" />
        <p className="font-medium text-gray-500">Aucun poste (truck slot) configure</p>
        <p className="text-xs mt-1">Configurez les postes dans l'onglet Ressources pour activer le suivi</p>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="space-y-5" data-testid="compliance-tab">
      {/* Month navigation + summary */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={prevMonth} data-testid="compliance-prev-month">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="text-base font-semibold text-gray-900 min-w-[160px] text-center">
            {MONTH_NAMES[month - 1]} {year}
          </span>
          <Button variant="ghost" size="sm" onClick={nextMonth} data-testid="compliance-next-month">
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
        <Button
          variant={groupByTransporter ? 'default' : 'outline'}
          size="sm"
          className="gap-2 h-8"
          onClick={() => setGroupByTransporter(!groupByTransporter)}
          data-testid="group-by-transporter-btn"
        >
          <Eye className="w-3.5 h-3.5" />
          Par transporteur
        </Button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-gray-500">Conformite globale</p>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-2xl font-bold text-gray-900">{data.globalComplianceRate}%</span>
              <ComplianceGauge rate={data.globalComplianceRate} />
            </div>
            <p className="text-[10px] text-gray-400 mt-0.5">{data.totalConformDays}/{data.totalExpectedDays} jours conformes</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <XCircle className="w-4 h-4 text-red-500" />
              <p className="text-xs text-gray-500">Absences</p>
            </div>
            <span className="text-2xl font-bold text-red-600">{data.deviations.absences}</span>
            <p className="text-[10px] text-gray-400">jours sans opération</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <ArrowRightLeft className="w-4 h-4 text-amber-500" />
              <p className="text-xs text-gray-500">Substitutions</p>
            </div>
            <span className="text-2xl font-bold text-amber-600">{data.deviations.substitutions}</span>
            <p className="text-[10px] text-gray-400">véhicule ou chauffeur different</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <TrendingDown className="w-4 h-4 text-orange-500" />
              <p className="text-xs text-gray-500">Sous-realisation</p>
            </div>
            <span className="text-2xl font-bold text-orange-600">{data.deviations.underPerformance}</span>
            <p className="text-[10px] text-gray-400">opérations manquantes</p>
          </CardContent>
        </Card>
      </div>

      {/* Problem slots alert */}
      {data.problemSlots?.length > 0 && data.problemSlots[0].rate < 80 && (
        <div className="flex items-start gap-3 p-3 bg-red-50 border border-red-200 rounded-lg">
          <AlertTriangle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-red-800">Postes les plus problematiques</p>
            <div className="flex flex-wrap gap-2 mt-1">
              {data.problemSlots.map(s => (
                <Badge key={s.slotId} variant="outline" className="text-xs text-red-600 border-red-300">
                  {s.label} — {s.rate}%
                </Badge>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Legend */}
      <div className="flex items-center gap-4 text-[10px] text-gray-500">
        {Object.entries(STATUS_CONFIG).filter(([k]) => k !== 'not_expected').map(([key, cfg]) => (
          <div key={key} className="flex items-center gap-1.5">
            <div className={`w-3.5 h-3.5 rounded ${cfg.color}`} />
            <span>{cfg.label}</span>
          </div>
        ))}
        <div className="flex items-center gap-1.5">
          <div className="w-3.5 h-3.5 rounded bg-gray-100 border border-gray-200" />
          <span>Non attendu</span>
        </div>
      </div>

      {/* Calendar grid */}
      {getSlotGroups().map((group, gi) => (
        <div key={gi} className="space-y-2">
          {group.transporter && (
            <div className="flex items-center justify-between px-2">
              <div className="flex items-center gap-2">
                <Truck className="w-4 h-4 text-gray-400" />
                <span className="text-sm font-semibold text-gray-700">{group.transporter}</span>
              </div>
              {group.totalExpected > 0 && (
                <ComplianceGauge rate={Math.round(group.totalConform / group.totalExpected * 100)} />
              )}
            </div>
          )}

          <Card>
            <CardContent className="p-2 overflow-x-auto">
              <table className="w-full border-collapse" data-testid="compliance-grid">
                <thead>
                  <tr>
                    <th className="text-left text-[10px] font-semibold text-gray-500 p-1 min-w-[140px] sticky left-0 bg-white z-10">Poste</th>
                    {dayNumbers.map(d => (
                      <th key={d} className="text-center text-[9px] text-gray-400 font-medium p-0.5 w-7">{d}</th>
                    ))}
                    <th className="text-center text-[10px] font-semibold text-gray-500 p-1 min-w-[60px]">Taux</th>
                  </tr>
                </thead>
                <tbody>
                  {group.slots.map(slot => {
                    const monthStr = `${year}-${String(month).padStart(2, '0')}`;
                    return (
                      <tr key={slot.slotId} className="border-t border-gray-50">
                        <td className="p-1 sticky left-0 bg-white z-10">
                          <div className="min-w-[130px]">
                            <p className="text-xs font-medium text-gray-800 truncate">{slot.label}</p>
                            <p className="text-[9px] text-gray-400 truncate">
                              {slot.assignedVehicles?.join(', ') || '—'}
                              {slot.assignedDrivers?.length > 0 && ` / ${slot.assignedDrivers.join(', ')}`}
                            </p>
                          </div>
                        </td>
                        {dayNumbers.map(d => {
                          const dateStr = `${monthStr}-${String(d).padStart(2, '0')}`;
                          const dayData = slot.days?.[dateStr];
                          return <DayCell key={d} dayData={dayData} dayNum={d} />;
                        })}
                        <td className="p-1 text-center">
                          <ComplianceGauge rate={slot.complianceRate} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </div>
      ))}
    </div>
  );
}
